/* =============================================================================
   DAILE Mono — DAILE Friday offer pop-up (R7). Markup: sections/mn-offer.liquid (footer group).

   Public API (also used by the homepage hero form):
     window.mnOffer.open()                     — open the pop-up
     window.mnOffer.close()                    — close it
     window.mnOffer.subscribe(email, {source}) — Promise: resolves { ok: true, via: 'klaviyo' | 'shopify' },
                                                 rejects with an Error whose .message is shown to the visitor
     window.mnOffer.config                     — { company, list, event, sourcePrefix, tags }
   Events on document: 'mn-offer:ready', 'mn-offer:open', 'mn-offer:close', 'mn-offer:subscribed' (detail.source).

   Sign-up: Klaviyo list set in the section → client subscription (custom_source "DAILE Friday <source>",
   profile property daile_friday_source) + client event "DAILE Friday signup". No list → Shopify customer form
   (/contact, tags newsletter,daile-friday, accepts marketing) by fetch; if Shopify answers with a captcha
   challenge, the real form is submitted.

   When it opens by itself: once per session, after the delay AND the scroll depth (or desktop exit-intent),
   not on cart / customizer / account pages, not while the cookie banner is open (waits for it), not for N
   days after closing, never after a sign-up. ?mn_offer=1 forces it (owner preview).
   Storage (wrapped in try/catch): localStorage 'mn-offer' { sub, closed, pillOff }, sessionStorage 'mn-offer-shown'.
   ============================================================================= */
(() => {
  'use strict';

  const d = document;
  const html = d.documentElement;
  const root = d.querySelector('[data-mn-offer]');
  if (!root || window.mnOffer) return;

  const DAY = 864e5;
  const KEY = 'mn-offer';
  const SESSION_KEY = 'mn-offer-shown';
  const PENDING_KEY = 'mn-offer-pending';
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

  const data = (name, fallback) => {
    const v = root.getAttribute(`data-${name}`);
    return v == null || v === '' ? fallback : v;
  };
  const num = (name, fallback) => {
    const n = parseFloat(data(name, ''));
    return Number.isFinite(n) ? n : fallback;
  };

  const cfg = {
    enabled: data('enabled') === 'true',
    excluded: data('excluded') === 'true',
    design: data('design') === 'true' || !!(window.Shopify && window.Shopify.designMode),
    delay: Math.max(0, num('delay', 12)) * 1000,
    scroll: Math.min(100, Math.max(0, num('scroll', 40))) / 100,
    exit: data('exit') === 'true',
    days: Math.max(1, num('days', 3)),
    pill: data('pill') === 'true',
  };
  const config = Object.freeze({
    company: data('kl-company', ''),
    list: data('kl-list', ''),
    event: data('kl-event', 'DAILE Friday signup'),
    sourcePrefix: data('kl-source', 'DAILE Friday'),
    tags: data('tags', 'newsletter,daile-friday'),
  });
  const msg = {
    bad: data('msg-bad', 'Please enter a valid e-mail address.'),
    err: data('msg-err', 'Something went wrong. Please try again.'),
    sending: data('msg-sending', 'Sending…'),
  };

  /* Storage ------------------------------------------------------------------ */
  const store = {
    get() {
      try { return JSON.parse(window.localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return {}; }
    },
    set(patch) {
      try { window.localStorage.setItem(KEY, JSON.stringify(Object.assign(store.get(), patch))); } catch (e) { /* private mode */ }
    },
  };
  const sess = {
    get(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* */ } },
    del(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* */ } },
  };
  const isSubscribed = () => !!store.get().sub;
  const recent = (ts) => !!ts && Date.now() - ts < cfg.days * DAY;

  const emit = (name, detail) => {
    try { d.dispatchEvent(new CustomEvent(name, { detail })); } catch (e) { /* */ }
  };
  const userError = (text, cause) => {
    const e = new Error(text);
    e.userFacing = true;
    if (cause) e.cause = cause;
    return e;
  };

  /* Sign-up ------------------------------------------------------------------ */
  const lang = (html.getAttribute('lang') || 'en').slice(0, 2);

  const klaviyo = (path, payload) => fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(config.company)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
    body: JSON.stringify(payload),
  });

  const viaKlaviyo = (email, source) => klaviyo('subscriptions', {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: `${config.sourcePrefix} ${source}`,
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { daile_friday_source: source, daile_friday_locale: lang },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: config.list } } },
    },
  }).then((res) => {
    if (!res.ok) throw new Error(`klaviyo ${res.status}`);
    klaviyo('events', {
      data: {
        type: 'event',
        attributes: {
          properties: { source, locale: lang, page: window.location.pathname },
          metric: { data: { type: 'metric', attributes: { name: config.event } } },
          profile: { data: { type: 'profile', attributes: { email } } },
        },
      },
    }).catch(() => { /* the subscription already worked */ });
    return { ok: true, via: 'klaviyo' };
  });

  const shopifyForm = () => root.querySelector('form.mn-offer__shopify');

  const viaShopify = (email, source) => {
    const form = shopifyForm();
    if (!form) return Promise.reject(new Error('no customer form'));
    const emailField = form.querySelector('[name="contact[email]"]');
    if (emailField) emailField.value = email;
    const body = new URLSearchParams(new FormData(form));
    body.set('contact[email]', email);
    const action = (form.getAttribute('action') || '/contact').split('#')[0];
    return fetch(action, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }).then((res) => {
      if (/\/challenge\b/.test(res.url || '')) {
        // Captcha: let Shopify handle it with a real submit; we come back with ?customer_posted=true.
        sess.set(PENDING_KEY, source);
        form.submit();
        return new Promise(() => {});
      }
      if (!res.ok) throw new Error(`contact ${res.status}`);
      return { ok: true, via: 'shopify' };
    });
  };

  let pending = null;
  const subscribe = (email, opts) => {
    const source = String((opts && opts.source) || 'popup').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').slice(0, 40) || 'popup';
    const value = String(email || '').trim();
    if (!EMAIL_RE.test(value)) return Promise.reject(userError(msg.bad));
    if (pending) return pending;
    const run = config.company && config.list ? viaKlaviyo(value, source) : viaShopify(value, source);
    pending = run.then((result) => {
      markSubscribed(source);
      return result;
    }, (err) => {
      throw (err && err.userFacing) ? err : userError(msg.err, err);
    }).finally(() => { pending = null; });
    return pending;
  };

  /* Dialog --------------------------------------------------------------------- */
  const dlg = root.querySelector('[data-mn-offer-dialog]');
  const panel = dlg && dlg.querySelector('[data-mn-offer-panel]');
  const form = dlg && dlg.querySelector('[data-mn-offer-form]');
  const input = dlg && dlg.querySelector('[data-mn-offer-email]');
  const msgEl = dlg && dlg.querySelector('[data-mn-offer-msg]');
  const submit = dlg && dlg.querySelector('[data-mn-offer-submit]');
  const submitLabel = dlg && dlg.querySelector('[data-mn-offer-submit-label]');
  const step = dlg && dlg.querySelector('[data-mn-offer-step]');
  const done = dlg && dlg.querySelector('[data-mn-offer-done]');
  const doneTitle = dlg && dlg.querySelector('[data-mn-offer-done-title]');
  const pill = root.querySelector('[data-mn-offer-pill]');
  const media = dlg && dlg.querySelector('.mn-offer__img');
  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  let opener = null;
  let closing = false;
  let closeTimer = 0;

  const say = (text) => {
    if (!msgEl) return;
    msgEl.textContent = text || '';
    if (input) {
      if (text) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  };
  const setBusy = (busy) => {
    if (!submit) return;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (submitLabel) {
      if (busy) {
        submitLabel.setAttribute('data-label', submitLabel.textContent);
        submitLabel.textContent = msg.sending;
      } else if (submitLabel.hasAttribute('data-label')) {
        submitLabel.textContent = submitLabel.getAttribute('data-label');
      }
    }
  };
  const showDone = (focus) => {
    if (!dlg || !done) return;
    dlg.classList.add('is-done');
    if (step) step.hidden = true;
    done.hidden = false;
    if (focus && doneTitle) { try { doneTitle.focus({ preventScroll: true }); } catch (e) { /* */ } }
  };

  const focusables = () => Array.from(dlg.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
    .filter((el) => !el.disabled && !el.closest('[hidden]') && el.getClientRects().length);

  /* phone keyboard: keep the sheet above it */
  const vv = window.visualViewport;
  const fit = () => {
    if (!dlg || !vv) return;
    const kb = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    dlg.style.setProperty('--mn-offer-kb', `${kb}px`);
    dlg.style.setProperty('--mn-offer-vh', `${Math.round(vv.height)}px`);
    const typing = kb > 120 && window.innerWidth < 750;
    dlg.classList.toggle('is-kb', typing);
    if (typing && submit && d.activeElement === input) {
      requestAnimationFrame(() => { try { submit.scrollIntoView({ block: 'nearest' }); } catch (e) { /* */ } });
    }
  };
  const fitOn = () => { if (vv) { vv.addEventListener('resize', fit); vv.addEventListener('scroll', fit); fit(); } };
  const fitOff = () => {
    if (!vv) return;
    vv.removeEventListener('resize', fit);
    vv.removeEventListener('scroll', fit);
    dlg.classList.remove('is-kb');
    dlg.style.removeProperty('--mn-offer-kb');
    dlg.style.removeProperty('--mn-offer-vh');
  };

  const open = (opts) => {
    if (!dlg || dlg.open) return;
    opener = d.activeElement && d.activeElement !== d.body ? d.activeElement : null;
    clearTimeout(closeTimer);
    closing = false;
    if (media && media.loading === 'lazy') media.loading = 'eager';
    say('');
    if (isSubscribed()) showDone(false);
    try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
    html.classList.add('mn-lock', 'mn-offer-open');
    hidePill();
    fitOn();
    // two frames so the slide-in transition runs from the closed position
    requestAnimationFrame(() => requestAnimationFrame(() => dlg.classList.add('is-open')));
    // Desktop: straight into the field. Touch: focus the panel (no keyboard jumping up uninvited).
    const target = (!isSubscribed() && input && finePointer() && !(opts && opts.noFocus)) ? input : panel;
    try { target.focus({ preventScroll: true }); } catch (e) { /* */ }
    sess.set(SESSION_KEY, '1');
    emit('mn-offer:open', { auto: !!(opts && opts.auto) });
  };

  const afterClose = () => {
    closing = false;
    dlg.classList.remove('is-open');
    html.classList.remove('mn-lock', 'mn-offer-open');
    fitOff();
    if (!isSubscribed()) store.set({ closed: Date.now() });
    updatePill();
    const back = opener;
    opener = null;
    if (back && back.isConnected && back.getClientRects().length) {
      try { back.focus({ preventScroll: true }); } catch (e) { /* */ }
    } else if (dlg.contains(d.activeElement)) {
      d.activeElement.blur();
    }
    emit('mn-offer:close', {});
  };

  const close = () => {
    if (!dlg || !dlg.open || closing) return;
    closing = true;
    dlg.classList.remove('is-open');
    closeTimer = setTimeout(() => {
      if (dlg.open) {
        try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); afterClose(); }
      }
    }, reduceMotion() ? 0 : 320);
  };

  if (dlg) {
    dlg.addEventListener('close', () => { clearTimeout(closeTimer); afterClose(); });
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-mn-offer-close]')) { e.preventDefault(); close(); }
    });
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) { e.preventDefault(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && (d.activeElement === first || d.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = (input && input.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        say(msg.bad);
        if (input) input.focus();
        return;
      }
      say('');
      setBusy(true);
      subscribe(email, { source: 'popup' })
        .then(() => { setBusy(false); showDone(true); })
        .catch((err) => {
          setBusy(false);
          say((err && err.message) || msg.err);
          if (input) input.focus();
        });
    });
    if (input) input.addEventListener('input', () => { if (msgEl && msgEl.textContent) say(''); });
  }

  /* Pill ---------------------------------------------------------------------- */
  const pillAllowed = () => !!pill && cfg.enabled && cfg.pill && !cfg.excluded && !isSubscribed() && !html.classList.contains('mn-df');
  function hidePill() { if (pill) pill.hidden = true; }
  function updatePill() {
    if (!pill) return;
    const st = store.get();
    const show = pillAllowed() && !!st.closed && !recent(st.pillOff) && !(dlg && dlg.open);
    pill.hidden = !show;
    if (show) lift();
  }
  // keep the pill above the PDP sticky bar / cookie banner on phones
  const BARS = '.mn-pdp-bar, .mn-com-bar, [data-daile-cookie]';
  function lift() {
    if (!pill || pill.hidden) return;
    let h = 0;
    d.querySelectorAll(BARS).forEach((el) => {
      if (el.hidden) return;
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') return;
      const r = el.getBoundingClientRect();
      if (r.height && r.top < window.innerHeight - 2 && r.bottom >= window.innerHeight - 4) h = Math.max(h, window.innerHeight - r.top);
    });
    pill.style.setProperty('--mn-offer-lift', `${Math.round(h)}px`);
    pill.classList.toggle('is-lifted', h > 0);
  }
  if (pill) {
    const later = () => { lift(); setTimeout(lift, 480); };
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(later);
      d.querySelectorAll(BARS).forEach((el) => mo.observe(el, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] }));
    }
    window.addEventListener('resize', later, { passive: true });
    pill.addEventListener('click', (e) => {
      if (e.target.closest('[data-mn-offer-pill-hide]')) {
        store.set({ pillOff: Date.now() });
        hidePill();
        return;
      }
      if (e.target.closest('[data-mn-offer-open]')) open({ manual: true });
    });
  }

  function markSubscribed(source) {
    store.set({ sub: Date.now(), src: source });
    sess.del(PENDING_KEY);
    hidePill();
    emit('mn-offer:subscribed', { source });
  }

  /* Auto-open ------------------------------------------------------------------ */
  const forced = /[?&]mn_offer=1(?:&|$)/.test(window.location.search);
  const pathExcluded = /\/(cart|checkouts?|account|challenge|password|orders|thank[_-]?you)(\/|$)|\/pages\/customizer/.test(window.location.pathname);
  const excluded = cfg.excluded || pathExcluded;

  const cookieBox = d.querySelector('[data-daile-cookie]');
  const cookieOpen = () => !!cookieBox && !cookieBox.hidden;
  const busy = () => {
    if (html.classList.contains('mn-lock')) return true;
    if (d.querySelector('dialog[open]:not([data-mn-offer-dialog])')) return true;
    const cart = d.getElementById('daile-cart');
    if (cart && cart.getAttribute('aria-hidden') === 'false') return true;
    const a = d.activeElement;
    return !!(a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !a.closest('[data-mn-offer]'));
  };

  // Wait for the cookie banner to be answered, then for other overlays / typing to finish.
  const whenClear = (fn) => {
    if (cookieOpen()) {
      const mo = new MutationObserver(() => {
        if (!cookieOpen()) { mo.disconnect(); setTimeout(() => whenClear(fn), 1500); }
      });
      mo.observe(cookieBox, { attributes: true, attributeFilter: ['hidden'] });
      return;
    }
    if (busy()) { setTimeout(() => whenClear(fn), 3000); return; }
    fn();
  };

  let fired = false;
  const canAuto = () => {
    const st = store.get();
    // html.mn-df: the visitor already has a DAILE Friday code applied (assets/mn-dfriday.js) → no sign-up pop-up.
    return cfg.enabled && !!dlg && !excluded && !cfg.design && !st.sub && !sess.get(SESSION_KEY) && !recent(st.closed) && !html.classList.contains('mn-df');
  };
  const fire = (why) => {
    if (fired || !canAuto()) return;
    fired = true;
    whenClear(() => { if (canAuto()) open({ auto: true, why }); });
  };

  const arm = () => {
    if (!canAuto()) return;
    let timeOk = cfg.delay === 0;
    let scrollOk = false;
    const depth = () => {
      const max = d.documentElement.scrollHeight - window.innerHeight;
      if (max < 200) return 1; // page too short to scroll — the delay alone decides
      return window.scrollY / max;
    };
    const check = () => { if (timeOk && scrollOk) { cleanup(); fire('time+scroll'); } };
    let ticking = false;
    const onScroll = () => {
      if (ticking || scrollOk) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (depth() >= cfg.scroll) { scrollOk = true; check(); }
      });
    };
    const onOut = (e) => {
      if (e.relatedTarget || e.clientY > 8) return;
      if (performance.now() < 4000) return;
      cleanup();
      fire('exit');
    };
    const cleanup = () => {
      window.removeEventListener('scroll', onScroll);
      d.removeEventListener('mouseout', onOut);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    if (cfg.exit && finePointer()) d.addEventListener('mouseout', onOut);
    /* Conversion R1 (22.10, analytics: artist product pages lose people in 2–19 s; the pop-up opened at 12 s + 40 %
       scroll — exactly in that window): on product pages it waits at least 35 s and for 60 % of the page; leaving
       with the mouse (desktop) still opens it. Everywhere else the settings stay. */
    const onProduct = /\/products\//.test(window.location.pathname);
    const wait = onProduct ? Math.max(cfg.delay, 35000) : cfg.delay;
    if (onProduct) cfg.scroll = Math.max(cfg.scroll || 0, 0.6);
    setTimeout(() => { timeOk = true; if (depth() >= cfg.scroll) scrollOk = true; check(); }, wait);
    // warm the image a little before the earliest moment it can open
    if (media) setTimeout(() => { if (canAuto() && media.loading === 'lazy') media.loading = 'eager'; }, Math.max(0, cfg.delay - 3000));
  };

  /* Back from a captcha submit (?customer_posted=true) ---------------------------- */
  const backFromChallenge = () => {
    const src = sess.get(PENDING_KEY);
    if (!src) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return false;
    markSubscribed(src);
    try {
      params.delete('customer_posted');
      const q = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash.replace(/^#(contact_form|mn-offer-[\w-]+)$/, ''));
    } catch (e) { /* */ }
    if (src === 'popup' && dlg) { showDone(false); open({ noFocus: true }); if (doneTitle) { try { doneTitle.focus({ preventScroll: true }); } catch (e) { /* */ } } }
    return true;
  };

  /* Theme editor: open while the section (or a tier block) is selected ----------- */
  if (cfg.design && dlg) {
    const mine = (e) => root.closest('.shopify-section') && e.target === root.closest('.shopify-section');
    d.addEventListener('shopify:section:select', (e) => { if (mine(e)) open({ noFocus: true }); });
    d.addEventListener('shopify:section:deselect', (e) => { if (mine(e)) close(); });
    d.addEventListener('shopify:block:select', (e) => { if (root.contains(e.target)) open({ noFocus: true }); });
  }

  /* Start ---------------------------------------------------------------------- */
  window.mnOffer = Object.freeze({ open, close, subscribe, config });
  emit('mn-offer:ready', { config });

  if (cfg.enabled && dlg) {
    if (!backFromChallenge()) {
      if (forced) setTimeout(() => open({ auto: true, why: 'forced' }), 700);
      else arm();
    }
    if (!forced && !excluded && !cfg.design) updatePill();
  }
})();
